import React, { useState } from 'react';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../hooks/useFetch';
import { isIOS } from '../lib/platform';

/**
 * Detect when the page is running inside a native iOS WebView (the App
 * Store reviewer's iPad runs the app this way). Popup-mode Sign in with
 * Apple does NOT work in WKWebView — window.open is blocked or the
 * postMessage handshake silently drops. We must use redirect (form_post)
 * mode in that environment.
 */
function isAppleWebViewContext() {
  if (typeof window === 'undefined') return false;
  if (!isIOS()) return false;
  // WebToNative injects window.WTN. Custom WKWebView wrappers usually
  // expose window.webkit.messageHandlers. Either is a strong native
  // signal. We also fall back to true for *any* iOS device, because
  // popup-mode SIWA is unreliable on iOS Safari 17+ as well.
  return true;
}

/**
 * Sign in with Apple button.
 *
 * Uses Apple's JS SDK (loaded in index.html) in popup mode.
 * On success Apple returns { authorization: { id_token, code }, user? }
 * which we post to /api/auth/apple/exchange to get our own JWT.
 *
 * Required to satisfy App Store guideline 4.8 (must offer Sign in with Apple
 * if any other 3rd-party login is offered).
 */
export const SignInWithAppleButton = ({ className = '' }) => {
  const [loading, setLoading] = useState(false);

  // Apple's JS SDK needs explicit init() before signIn() can work reliably
  // inside native WebViews (WebToNative wraps iOS in WKWebView). Without
  // this, the button can appear unresponsive on newer iOS versions.
  const ensureAppleInit = () => {
    if (typeof window === 'undefined' || !window.AppleID?.auth?.init) return false;
    if (window.__appleIdInited) return true;
    try {
      const metaClientId = document.querySelector(
        'meta[name="appleid-signin-client-id"]'
      )?.getAttribute('content');
      const clientId =
        process.env.REACT_APP_APPLE_CLIENT_ID ||
        metaClientId ||
        'com.clearcommunicationapp.www';
      const metaRedirect = document.querySelector(
        'meta[name="appleid-signin-redirect-uri"]'
      )?.getAttribute('content');
      // Apple's Service ID registration only accepts ONE canonical Return URL.
      // We registered https://www.clearcommunicationapp.com/api/auth/apple/callback.
      // If the user happens to land on the apex domain (no www) or a
      // preview origin, deriving from window.location.origin produces a
      // redirect URI Apple doesn't recognise → "Your request could not be
      // completed because of an error." Hard-coding the canonical URL
      // fixes this without breaking the popup flow.
      const redirectURI =
        process.env.REACT_APP_APPLE_REDIRECT_URI ||
        metaRedirect ||
        'https://www.clearcommunicationapp.com/api/auth/apple/callback';
      // On iOS (especially WKWebView) popup mode fails silently. Force
      // redirect/form_post flow so Apple navigates the WebView to
      // appleid.apple.com and POSTs back to /api/auth/apple/callback.
      const useRedirect = isAppleWebViewContext();
      window.AppleID.auth.init({
        clientId,
        scope: 'name email',
        redirectURI,
        state: 'ccapp-' + Math.random().toString(36).slice(2, 10),
        usePopup: !useRedirect,
      });
      window.__appleIdInited = true;
      window.__appleIdUsesRedirect = useRedirect;
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AppleID init failed:', err);
      return false;
    }
  };

  const handleClick = async () => {
    if (typeof window === 'undefined' || !window.AppleID) {
      toast.error('Apple Sign-In not available. Please try another option.');
      return;
    }

    // CRITICAL: Apple's Service ID is registered for www.clearcommunicationapp.com.
    // If the user is on the apex domain (no "www"), Apple's popup will throw
    // "Your request could not be completed because of an error" because the
    // postMessage origin won't match. Force the user to www to fix this.
    try {
      const host = window.location.hostname;
      if (host === 'clearcommunicationapp.com') {
        toast.error('Redirecting to www.clearcommunicationapp.com for Apple Sign-In...');
        window.location.href = window.location.href.replace(
          'clearcommunicationapp.com',
          'www.clearcommunicationapp.com'
        );
        return;
      }
    } catch (_) {
      // continue — origin check is best-effort
    }

    if (!ensureAppleInit()) {
      toast.error('Apple Sign-In failed to load. Please try another option.');
      return;
    }
    setLoading(true);
    try {
      // In redirect mode signIn() navigates away and never resolves —
      // the user comes back at /auth/apple/callback (via backend 302).
      // We still call it; if redirect is in flight, no further code runs.
      const signInPromise = window.AppleID.auth.signIn();
      if (window.__appleIdUsesRedirect) {
        // Give Apple's SDK a beat to start the navigation; if it doesn't,
        // surface a clear error rather than hanging the button.
        await new Promise((r) => setTimeout(r, 1500));
        return;
      }
      const data = await signInPromise;
      const idToken = data?.authorization?.id_token;
      if (!idToken) throw new Error('No id_token returned from Apple');

      const { data: resp, error, status } = await apiFetch('/api/auth/apple/exchange', {
        method: 'POST',
        body: {
          id_token: idToken,
          user: data?.user || null,
        },
      });

      if (error || !resp?.token) {
        // eslint-disable-next-line no-console
        console.error('[Apple] Backend response:', { status, error });
        throw new Error(error || `Apple sign-in exchange failed (HTTP ${status})`);
      }

      // Persist token; reload routes through AuthContext.checkAuth
      localStorage.setItem('auth_token', resp.token);
      toast.success('Signed in with Apple');
      window.location.href = resp.is_new_user ? '/account?welcome=1' : '/library';
    } catch (e) {
      // Apple throws { error: 'popup_closed_by_user' } when the user cancels.
      // Other common values: 'user_cancelled_authorize', 'invalid_client',
      // 'invalid_request', 'unauthorized_client'. Show the real reason when
      // we have one so the user (and our future debugging) can act on it.
      if (e?.error === 'popup_closed_by_user' || e?.error === 'user_cancelled_authorize') {
        // Silent — the user just closed the dialog
      } else {
        // eslint-disable-next-line no-console
        console.error('Apple sign-in error:', e);
        const reason = e?.error || e?.message || '';
        // Apple's "Your request could not be completed because of an error"
        // is almost always a Service ID config mismatch. Be explicit so the
        // admin can fix it instead of guessing.
        let friendly = reason
          ? `Could not sign in with Apple: ${reason}`
          : 'Could not sign in with Apple. Please try again or use another sign-in option.';
        if (typeof reason === 'string' && /could not be completed/i.test(reason)) {
          friendly =
            'Apple Sign-In is misconfigured. Make sure the Service ID `com.clearcommunicationapp.www` at developer.apple.com lists both `clearcommunicationapp.com` and `www.clearcommunicationapp.com` as Domains, and `https://www.clearcommunicationapp.com/api/auth/apple/callback` as a Return URL.';
        }
        toast.error(friendly);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      disabled={loading}
      className={`w-full bg-black hover:bg-zinc-800 text-white border-black hover:text-white ${className}`}
      data-testid="apple-signin-btn"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.49 0-2.534-1.27-3.514-2.61-1.273-1.73-2.244-4.42-2.244-6.97 0-4.06 2.65-6.21 5.265-6.21 1.405 0 2.575.92 3.45.92.832 0 2.143-.97 3.71-.97.61 0 2.79.06 4.21 2.04-.12.07-2.485 1.45-2.485 4.45 0 3.46 3.07 4.62 3.18 4.65z" />
        </svg>
      )}
      Continue with Apple
    </Button>
  );
};

export default SignInWithAppleButton;
